import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Chip,
  Grid,
  Paper
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { superAdminService } from '../../../services/superAdmin/superAdminService';

const SignupAttemptsPage = () => {
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [statusFilter, setStatusFilter] = useState('');

  const { data: statsData } = useQuery({
    queryKey: ['superAdminSignupStats'],
    queryFn: async () => {
      const result = await superAdminService.getSignupAttemptStats();
      if (!result.success) throw new Error(result.message);
      return result.data;
    }
  });

  const { data, isLoading } = useQuery({
    queryKey: ['superAdminSignupAttempts', paginationModel.page, paginationModel.pageSize, statusFilter],
    queryFn: async () => {
      const result = await superAdminService.getSignupAttempts({
        pageNumber: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        status: statusFilter || undefined
      });
      if (!result.success) throw new Error(result.message);
      return result;
    }
  });

  const stats = statsData || {};

  const columns = [
    { field: 'Username', headerName: 'Email', flex: 1, minWidth: 200 },
    { field: 'CompanyName', headerName: 'Company', width: 180 },
    { field: 'FirstName', headerName: 'First Name', width: 120 },
    { field: 'LastName', headerName: 'Last Name', width: 120 },
    {
      field: 'Status',
      headerName: 'Status',
      width: 110,
      renderCell: ({ value }) => (
        <Chip
          label={value || 'unknown'}
          size="small"
          color={
            value === 'success' ? 'success' :
            value === 'failed' ? 'error' :
            value === 'pending' ? 'warning' :
            'default'
          }
          sx={{ fontSize: '0.7rem', textTransform: 'capitalize' }}
        />
      )
    },
    { field: 'PaymentMethod', headerName: 'Payment', width: 120 },
    { field: 'PlanName', headerName: 'Plan', width: 120 },
    {
      field: 'Amount',
      headerName: 'Amount',
      width: 100,
      renderCell: ({ row }) => row.Amount ? `${row.Currency || 'AED'} ${Number(row.Amount).toLocaleString()}` : ''
    },
    {
      field: 'CreatedAt',
      headerName: 'Date',
      width: 150,
      valueFormatter: (params) => {
        const val = params?.value ?? params;
        return val ? new Date(val).toLocaleString() : '';
      }
    }
  ];

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>Signup Attempts</Typography>

      {/* Status Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {[
          { label: 'Success', value: stats.success || 0, color: '#10b981' },
          { label: 'Pending', value: stats.pending || 0, color: '#f59e0b' },
          { label: 'Failed', value: stats.failed || 0, color: '#ef4444' },
          { label: 'Abandoned', value: stats.abandoned || 0, color: '#94a3b8' }
        ].map(({ label, value, color }) => (
          <Grid item xs={6} sm={3} key={label}>
            <Paper sx={{
              p: 2,
              borderRadius: 2,
              border: '1px solid #e2e8f0',
              boxShadow: 'none',
              textAlign: 'center'
            }}>
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color }}>{value}</Typography>
              <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>{label}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <TextField
          select
          size="small"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          sx={{ minWidth: 130 }}
          InputProps={{ sx: { borderRadius: 2, backgroundColor: 'white' } }}
        >
          <MenuItem value="">All Statuses</MenuItem>
          <MenuItem value="success">Success</MenuItem>
          <MenuItem value="pending">Pending</MenuItem>
          <MenuItem value="failed">Failed</MenuItem>
          <MenuItem value="abandoned">Abandoned</MenuItem>
        </TextField>
      </Box>

      <Box sx={{ height: 'calc(100vh - 340px)', backgroundColor: 'white', borderRadius: 3, border: '1px solid #e2e8f0' }}>
        <DataGrid
          rows={data?.data || []}
          columns={columns}
          loading={isLoading}
          getRowId={(row) => row.Id}
          rowCount={data?.totalCount || 0}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50]}
          disableRowSelectionOnClick
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': { backgroundColor: '#f8fafc' }
          }}
        />
      </Box>
    </Box>
  );
};

export default SignupAttemptsPage;
